import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
  action
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle className="w-5 h-5 flex-shrink-0" />,
          iconColor: '#10b981',
          bgColor: '#10b981',
          borderColor: 'rgba(16, 185, 129, 0.2)'
        };
      case 'error':
        return {
          icon: <XCircle className="w-5 h-5 flex-shrink-0" />,
          iconColor: '#ef4444',
          bgColor: '#ef4444',
          borderColor: 'rgba(239, 68, 68, 0.2)'
        };
      case 'warning':
        return {
          icon: <AlertCircle className="w-5 h-5 flex-shrink-0" />,
          iconColor: '#f59e0b',
          bgColor: '#f59e0b',
          borderColor: 'rgba(245, 158, 11, 0.2)'
        };
      case 'info':
      default:
        return {
          icon: <Info className="w-5 h-5 flex-shrink-0" />,
          iconColor: '#2563eb',
          bgColor: '#2563eb',
          borderColor: 'rgba(37, 99, 235, 0.2)'
        };
    }
  };

  const styles = getStyles();

  return (
    <div
      className="border rounded-lg shadow-lg p-4 mb-3 min-w-[320px] max-w-md animate-slide-in-right bg-white"
      role="alert"
      style={{
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderColor: styles.borderColor
      }}
    >
      <div className="flex items-start gap-3">
        <div style={{ color: styles.iconColor }}>
          {styles.icon}
        </div>
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">
              {title}
            </h4>
          )}
          <p className="text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed">
            {message}
          </p>
          {action && (
            <button
              onClick={() => {
                action.onClick();
                onClose(id);
              }}
              style={{ color: styles.bgColor }}
              className="mt-2 text-sm font-medium hover:underline transition-all"
            >
              {action.label}
            </button>
          )}
        </div>
        <button
          onClick={() => onClose(id)}
          className="text-gray-400 hover:text-gray-600 transition-all flex-shrink-0 p-1"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
